import { getTrackersDataStream, sendMinifinderCommand } from "helpers/backend_helper"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState, useMemo } from "react"
import { withTranslation } from "react-i18next"
import { Col, Row, Button, CardBody, Card, Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap"
import { showToast, defaultPageSizeOptions, stringSorter, dateFormatter, dateSorter } from "utils/general"
import CustomRangePicker from "components/DatePickers/RangePicker"
import { Table as AntdTable, Input as AntdInput, AutoComplete, InputNumber } from "antd"
import { tableDefaultHeight } from "constants/layout"

const { Search } = AntdInput

const TrackerDataStreamTab = props => {
  const [entries, setEntries] = useState([])
  const [commandOptions] = useState([
    { value: "cmd:loc" },
    { value: "cmd:getParams" },

  ])
  const [inputMessage, setInputMessage] = useState("")
  const [refreshing, setRefreshing] = useState(false)
  const [sending, setSending] = useState(false)
  const [maxItems, setMaxItems] = useState("500")
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, "days"),
    dayjs(),
  ])
  const [searchText, setSearchText] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [selectedData, setSelectedData] = useState("")
  const [searchTriggers, setSearchTriggers] = useState(0) // Delayed search

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    getMessages()
  }, [props.imei, dateRange, searchTriggers])

  // Get data messages
  const getMessages = () => {
    if (props.imei?.length > 0 && dateRange && !refreshing) {
      setRefreshing(true)
      let rows = !Number.isNaN(Number.parseFloat(maxItems)) ? Number.parseInt(maxItems, 10) : 500
      getTrackersDataStream(
        abortRef.current,
        props.imei,
        dateRange[0].valueOf(),
        dateRange[1].valueOf(),
        searchText,
        rows
      )
        .then(data => {
          if (data.entries) {
            setEntries(data.entries)
          }
        })
        .catch(e => {
          console.log(e)
        })
        .finally(() => {
          setRefreshing(false)
        })
    }
  }

  // Send command to tracker
  const sendCommand = () => {
    if (
      inputMessage.length > 0 &&
      props.imei &&
      !sending
    ) {
      setSending(true)
      sendMinifinderCommand(
        abortRef.current,
        props.imei,
        inputMessage
      )
        .then(response => {
          console.log("Send response: ", response)
          showToast(props.t("Command sent"), "success")
          setDateRange([dateRange[0], dayjs()])
        })
        .catch(e => {
          showToast(props.t("Error"), "error")
          console.log(e)
        })
        .finally(() => {
          setSending(false)
        })
    }
  }

  // Table columns
  const columns = useMemo(
    () => [
      {
        dataIndex: "timestamp",
        title: props.t("Time"),
        sorter: (a, b, sortOrder) => {
          return dateSorter(
            a["timestamp"],
            b["timestamp"],
            sortOrder
          )
        },
        render: dateFormatter,
        width: "150px",
        ellipsis: true,
      },
      {
        dataIndex: "sequence",
        title: props.t("Sequence"),
        sorter: (a, b, sortOrder) => {
          return stringSorter(a, b, "sequence", sortOrder)
        },
        width: "100px",
        ellipsis: true,
      },
      {
        dataIndex: "data",
        title: props.t("Message"),
        sorter: (a, b, sortOrder) => {
          return stringSorter(a, b, "data", sortOrder)
        },
        width: "700px",
        ellipsis: true,

      },
      {
        dataIndex: "source",
        title: props.t("Source"),
        sorter: (a, b, sortOrder) => {
          return stringSorter(a, b, "source", sortOrder)
        },
        width: "140px",
        ellipsis: true,
      },
    ],
    [props.t]
  )

  const onDateSelection = value => {
    setDateRange(value)
  }

  return (
    <>
      <Card>
        <CardBody>
          <Row>
            <Col className="mb-3 col-auto">
              <AutoComplete
                placeholder={props.t("Select or write a command")}
                value={inputMessage}
                style={{ minWidth: 360, width: "100%" }}
                options={commandOptions}
                defaultActiveFirstOption={false}
                allowClear
                onChange={v => {
                  setInputMessage(v)
                }}
              >
              </AutoComplete>
            </Col>
            <Col className="mb-3 col-auto">
              <Button
                color="primary"
                className="compact-button"
                disabled={
                  sending || !props.imei || !inputMessage
                }
                onClick={() => {
                  sendCommand()
                }}
              >
                <i className="mdi mdi-send me-2" />
                {props.t("Send")}
              </Button>
            </Col>
          </Row>
          <Row>
            <Col className="col-12">
              <React.Fragment>
                <Row>
                  <Col className="mb-3" style={{ maxWidth: "320px" }}>
                    <Search
                      placeholder={props.t("Message") + " / " + props.t("Source")}
                      disabled={refreshing}
                      allowClear
                      value={searchText}
                      onChange={v => {
                        setSearchText(v.target.value)
                      }}
                      onSearch={() => {
                        // onSearch triggers before onChange on clear, delay data fetch a bit
                        setSearchTriggers(prevValue => prevValue + 1)
                      }}
                      style={{ minWidth: "180px", width: "100%" }}
                    />
                  </Col>
                  <Col className="col-auto mb-3">
                    <InputNumber
                      style={{ width: "135px" }}
                      suffix={props.t("max")}
                      disabled={refreshing}
                      value={maxItems}
                      step={100}
                      onChange={newVal => {
                        setMaxItems(newVal)
                      }}
                      onPressEnter={() => {
                        getMessages()
                      }}
                    />
                  </Col>
                  <Col style={{ maxWidth: "450px" }}>
                    <div className="mb-3" style={{ minWidth: "350px" }}>
                      <CustomRangePicker
                        value={dateRange}
                        rangeChanged={onDateSelection}
                        showTime={true}
                        allowClear={false}
                      />
                    </div>
                  </Col>
                  <Col className="col-auto">
                    <Button
                      className="d-flex align-items-center compact-button"
                      color="primary"
                      disabled={refreshing}
                      onClick={() => {
                        setDateRange([dateRange[0], dayjs()])
                      }}
                    >
                      <i className="mdi mdi-calendar-refresh me-2" />
                      {props.t("Refresh time")}
                    </Button>
                  </Col>
                </Row>
                <Row className="mt-1">
                  <AntdTable
                    size="small"
                    bordered
                    className="colored-header narrow-header"
                    loading={refreshing}
                    columns={columns}
                    dataSource={entries}
                    pagination={{
                      showTotal: (total) => `${total} ${props.t("found results")}`,
                      showSizeChanger: true,
                      defaultPageSize: 200,
                      pageSizeOptions: defaultPageSizeOptions,
                    }}
                    onRow={(record, rowIndex) => {
                      return {
                        onClick: event => {
                          setSelectedData(record.data)
                          try {
                            let json = JSON.parse(record.data)
                            setSelectedData(JSON.stringify(json, null, 4))
                          }
                          catch (err) { console.log(err) }
                          setShowModal(true)
                        },
                      }
                    }}
                    //tableLayout="auto"
                    scroll={{
                      //x: "max-content",
                      y: tableDefaultHeight,
                    }}
                  />
                </Row>
              </React.Fragment>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Modal
        isOpen={showModal}
        toggle={() => {
          setShowModal(!showModal)
        }}
        centered
        size="lg"
      >
        <ModalHeader>{props.t("Message")}</ModalHeader>
        <ModalBody>
          <Input
            type="textarea"
            disabled
            rows={30}
            value={selectedData || ""}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              setShowModal(false)
            }}
          >
            {props.t("Close")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

TrackerDataStreamTab.propTypes = {
  t: PropTypes.any,
  imei: PropTypes.string,
}

export default withTranslation()(TrackerDataStreamTab)